/**
 * Wizard Session Manager for Knowledge Base Configuration
 * Handles session-based storage and management of KB configuration during wizard flow
 * Integrates with existing WizardStateManager for complete session management
 */

import React from 'react';
import { WizardStateManager } from './wizardStateManager.js';
import { getGlobalApiClient } from './consolidatedApiClient.js';
import { StandardizedError, ERROR_CATEGORIES, ERROR_SEVERITY } from './consolidatedErrorHandling.js';

/**
 * Knowledge Base Session Manager
 * Extends wizard state management with KB-specific functionality
 */
export class WizardSessionManager extends WizardStateManager {
  constructor(sessionId = null, parrotId = null, apiClient = null) {
    super(sessionId, parrotId);
    
    this.apiClient = apiClient || getGlobalApiClient(() => this.getAuthToken());
    this.kbStorageKey = `${this.storageKey}_kb_config`;
    this.kbValidationCache = new Map();
    
    // KB configuration structure
    this.defaultKbConfig = {
      files: [],
      urls: [],
      textContent: [],
      metadata: {
        last_updated: null,
        total_files: 0,
        total_urls: 0,
        total_text_items: 0,
        validation_status: 'pending'
      }
    };
    
    // Initialize KB configuration if not exists
    this.initializeKbConfig();
  }

  /**
   * Get authentication token (to be implemented by parent component)
   * This should be overridden or injected by the consuming component
   */
  async getAuthToken() {
    // This will be provided by the component using this manager
    throw new Error('getAuthToken must be implemented by the consuming component');
  }

  /**
   * Set the authentication token getter function
   * @param {Function} tokenGetter - Function that returns the auth token
   */
  setAuthTokenGetter(tokenGetter) {
    this.getAuthToken = tokenGetter;
  }

  /**
   * Initialize KB configuration in session storage
   */
  initializeKbConfig() {
    try {
      const existingConfig = this.getKnowledgeBaseConfig();
      if (!existingConfig) {
        this.saveKnowledgeBaseConfig(this.defaultKbConfig);
      }
    } catch (error) {
      console.error('Failed to initialize KB configuration:', error);
    }
  }

  /**
   * Get current knowledge base configuration from session
   * @returns {object|null} KB configuration object
   */
  getKnowledgeBaseConfig() {
    try {
      const configJson = localStorage.getItem(this.kbStorageKey);
      return configJson ? JSON.parse(configJson) : null;
    } catch (error) {
      console.error('Failed to get KB configuration:', error);
      return null;
    }
  }

  /**
   * Save knowledge base configuration to session
   * @param {object} config - KB configuration object
   * @returns {boolean} Success status
   */
  saveKnowledgeBaseConfig(config) {
    try {
      const timestamp = new Date().toISOString();
      const updatedConfig = {
        ...config,
        metadata: {
          ...config.metadata,
          last_updated: timestamp
        }
      };

      localStorage.setItem(this.kbStorageKey, JSON.stringify(updatedConfig));
      
      // Update main wizard state to indicate KB changes
      const currentState = this.getState() || {};
      this.saveState(currentState.currentStep || 1, {
        ...currentState[`step${currentState.currentStep || 1}Data`],
        knowledge_base_updated: timestamp
      });

      // Notify listeners
      this.notifyChangeListeners('kb_config_updated', { config: updatedConfig });
      
      return true;
    } catch (error) {
      console.error('Failed to save KB configuration:', error);
      return false;
    }
  }

  /**
   * Add file to KB configuration
   * @param {object} fileData - File data object
   * @returns {Promise<boolean>} Success status
   */
  async addFile(fileData) {
    try {
      const config = this.getKnowledgeBaseConfig() || this.defaultKbConfig;
      
      // Generate unique ID for the file
      const fileId = `temp_file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const fileEntry = {
        id: fileId,
        name: fileData.name,
        size: fileData.size,
        type: fileData.type,
        r2_key: fileData.r2_key || null,
        status: fileData.status || 'pending',
        validation: {
          valid: true,
          errors: []
        },
        uploaded_at: new Date().toISOString()
      };

      // Add to files array
      config.files.push(fileEntry);
      
      // Update metadata
      config.metadata.total_files = config.files.length;
      config.metadata.validation_status = 'pending';

      // Save updated configuration
      const success = this.saveKnowledgeBaseConfig(config);
      
      if (success) {
        this.notifyChangeListeners('kb_file_added', { file: fileEntry, config });
      }
      
      return success;
    } catch (error) {
      console.error('Failed to add file to KB configuration:', error);
      throw new StandardizedError({
        message: 'Failed to add file to knowledge base configuration',
        code: 'KB_FILE_ADD_ERROR',
        category: ERROR_CATEGORIES.VALIDATION,
        severity: ERROR_SEVERITY.MEDIUM,
        originalError: error
      });
    }
  }

  /**
   * Remove file from KB configuration
   * @param {string} fileId - File ID to remove
   * @returns {boolean} Success status
   */
  removeFile(fileId) {
    try {
      const config = this.getKnowledgeBaseConfig() || this.defaultKbConfig;
      
      // Find and remove the file
      const fileIndex = config.files.findIndex(file => file.id === fileId);
      if (fileIndex === -1) {
        return false;
      }

      const removedFile = config.files.splice(fileIndex, 1)[0];
      
      // Update metadata
      config.metadata.total_files = config.files.length;

      // Save updated configuration
      const success = this.saveKnowledgeBaseConfig(config);
      
      if (success) {
        this.notifyChangeListeners('kb_file_removed', { file: removedFile, config });
      }
      
      return success;
    } catch (error) {
      console.error('Failed to remove file from KB configuration:', error);
      return false;
    }
  }

  /**
   * Add URL to KB configuration with validation
   * @param {string} url - URL to add
   * @returns {Promise<object>} Result object with success status and validation info
   */
  async addUrl(url) {
    try {
      const config = this.getKnowledgeBaseConfig() || this.defaultKbConfig;
      
      // Validate URL format first
      const formatValidation = this.validateUrlFormat(url);
      if (!formatValidation.valid) {
        throw new StandardizedError({
          message: formatValidation.error,
          code: 'INVALID_URL_FORMAT',
          category: ERROR_CATEGORIES.VALIDATION,
          severity: ERROR_SEVERITY.LOW
        });
      }

      // Generate unique ID for the URL
      const urlId = `url_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const urlEntry = {
        id: urlId,
        url: url,
        status: 'validating',
        validation: {
          valid: false,
          accessible: false,
          last_checked: new Date().toISOString(),
          error: null
        },
        added_at: new Date().toISOString()
      };

      // Add to URLs array
      config.urls.push(urlEntry);
      
      // Update metadata
      config.metadata.total_urls = config.urls.length;
      config.metadata.validation_status = 'validating';

      // Save configuration with pending validation
      this.saveKnowledgeBaseConfig(config);
      
      // Perform accessibility validation asynchronously
      try {
        const accessibilityResult = await this.validateUrlAccessibility(url);
        
        // Update the URL entry with validation results
        const updatedConfig = this.getKnowledgeBaseConfig();
        const urlToUpdate = updatedConfig.urls.find(u => u.id === urlId);
        if (urlToUpdate) {
          urlToUpdate.status = accessibilityResult.accessible ? 'validated' : 'error';
          urlToUpdate.validation = {
            valid: true,
            accessible: accessibilityResult.accessible,
            last_checked: new Date().toISOString(),
            error: accessibilityResult.error || null
          };
          
          // Update overall validation status
          const allValid = updatedConfig.urls.every(u => u.validation.valid && u.validation.accessible);
          updatedConfig.metadata.validation_status = allValid ? 'valid' : 'partial';
          
          this.saveKnowledgeBaseConfig(updatedConfig);
          this.notifyChangeListeners('kb_url_validated', { url: urlToUpdate, config: updatedConfig });
        }
      } catch (validationError) {
        // Update URL entry with validation error
        const updatedConfig = this.getKnowledgeBaseConfig();
        const urlToUpdate = updatedConfig.urls.find(u => u.id === urlId);
        if (urlToUpdate) {
          urlToUpdate.status = 'error';
          urlToUpdate.validation.error = validationError.message;
          this.saveKnowledgeBaseConfig(updatedConfig);
          this.notifyChangeListeners('kb_url_validation_error', { url: urlToUpdate, error: validationError });
        }
      }

      this.notifyChangeListeners('kb_url_added', { url: urlEntry, config });
      
      return {
        success: true,
        urlId: urlId,
        url: urlEntry
      };
    } catch (error) {
      console.error('Failed to add URL to KB configuration:', error);
      throw error;
    }
  }

  /**
   * Remove URL from KB configuration
   * @param {string} urlId - URL ID to remove
   * @returns {boolean} Success status
   */
  removeUrl(urlId) {
    try {
      const config = this.getKnowledgeBaseConfig() || this.defaultKbConfig;
      
      // Find and remove the URL
      const urlIndex = config.urls.findIndex(url => url.id === urlId);
      if (urlIndex === -1) {
        return false;
      }

      const removedUrl = config.urls.splice(urlIndex, 1)[0];
      
      // Update metadata
      config.metadata.total_urls = config.urls.length;

      // Save updated configuration
      const success = this.saveKnowledgeBaseConfig(config);
      
      if (success) {
        this.notifyChangeListeners('kb_url_removed', { url: removedUrl, config });
      }
      
      return success;
    } catch (error) {
      console.error('Failed to remove URL from KB configuration:', error);
      return false;
    }
  }

  /**
   * Add text content to KB configuration
   * @param {object} textData - Text content data
   * @returns {boolean} Success status
   */
  addTextContent(textData) {
    try {
      const config = this.getKnowledgeBaseConfig() || this.defaultKbConfig;
      
      // Generate unique ID for the text content
      const textId = `text_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const textEntry = {
        id: textId,
        name: textData.name || 'Untitled Text',
        content: textData.content,
        status: 'created',
        created_at: new Date().toISOString()
      };

      // Add to text content array
      config.textContent.push(textEntry);
      
      // Update metadata
      config.metadata.total_text_items = config.textContent.length;

      // Save updated configuration
      const success = this.saveKnowledgeBaseConfig(config);
      
      if (success) {
        this.notifyChangeListeners('kb_text_added', { text: textEntry, config });
      }
      
      return success;
    } catch (error) {
      console.error('Failed to add text content to KB configuration:', error);
      return false;
    }
  }

  /**
   * Remove text content from KB configuration
   * @param {string} textId - Text content ID to remove
   * @returns {boolean} Success status
   */
  removeTextContent(textId) {
    try {
      const config = this.getKnowledgeBaseConfig() || this.defaultKbConfig;
      
      // Find and remove the text content
      const textIndex = config.textContent.findIndex(text => text.id === textId);
      if (textIndex === -1) {
        return false;
      }

      const removedText = config.textContent.splice(textIndex, 1)[0];
      
      // Update metadata
      config.metadata.total_text_items = config.textContent.length;

      // Save updated configuration
      const success = this.saveKnowledgeBaseConfig(config);
      
      if (success) {
        this.notifyChangeListeners('kb_text_removed', { text: removedText, config });
      }
      
      return success;
    } catch (error) {
      console.error('Failed to remove text content from KB configuration:', error);
      return false;
    }
  }

  /**
   * Validate URL format
   * @param {string} url - URL to validate
   * @returns {object} Validation result
   */
  validateUrlFormat(url) {
    try {
      // Check if URL is provided
      if (!url || typeof url !== 'string' || url.trim() === '') {
        return {
          valid: false,
          error: 'URL is required'
        };
      }

      // Check if URL starts with http:// or https://
      const trimmedUrl = url.trim();
      if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
        return {
          valid: false,
          error: 'Please enter a valid URL starting with http:// or https://'
        };
      }

      // Use URL constructor for more thorough validation
      try {
        new URL(trimmedUrl);
        return {
          valid: true,
          error: null
        };
      } catch (urlError) {
        return {
          valid: false,
          error: 'Please enter a valid URL format'
        };
      }
    } catch (error) {
      return {
        valid: false,
        error: 'URL validation failed'
      };
    }
  }

  /**
   * Validate URL accessibility
   * @param {string} url - URL to check accessibility
   * @returns {Promise<object>} Accessibility result
   */
  async validateUrlAccessibility(url) {
    try {
      // Check cache first
      const cacheKey = `url_validation_${url}`;
      if (this.kbValidationCache.has(cacheKey)) {
        const cached = this.kbValidationCache.get(cacheKey);
        // Use cached result if less than 5 minutes old
        if (Date.now() - cached.timestamp < 5 * 60 * 1000) {
          return cached.result;
        }
      }

      // Make accessibility check via backend API
      const response = await this.apiClient.post('/api/wizard/validate-url', {
        url: url
      }, {}, 'default');

      const result = {
        accessible: response.success && response.accessible,
        error: response.error || null,
        status_code: response.status_code || null
      };

      // Cache the result
      this.kbValidationCache.set(cacheKey, {
        result,
        timestamp: Date.now()
      });

      return result;
    } catch (error) {
      console.error('URL accessibility validation failed:', error);
      return {
        accessible: false,
        error: 'URL is not accessible - please check the address',
        status_code: null
      };
    }
  }

  /**
   * Sync KB configuration with backend session
   * @returns {Promise<boolean>} Success status
   */
  async syncWithBackend() {
    try {
      if (!this.sessionId) {
        // For editing mode (no session ID), skip backend sync
        // KB configuration will be handled during final save
        console.log('No session ID available - skipping backend sync (editing mode)');
        return true;
      }

      const kbConfig = this.getKnowledgeBaseConfig();
      if (!kbConfig) {
        return true; // Nothing to sync
      }

      // Send KB configuration to backend
      const response = await this.apiClient.post('/api/wizard/update-session-kb', {
        session_id: this.sessionId,
        knowledge_base_config: kbConfig
      }, {}, 'wizard-data');

      if (response.success) {
        this.notifyChangeListeners('kb_synced_with_backend', { config: kbConfig });
        return true;
      } else {
        throw new StandardizedError({
          message: response.error || 'Failed to sync KB configuration with backend',
          code: 'BACKEND_SYNC_ERROR',
          category: ERROR_CATEGORIES.SYSTEM,
          severity: ERROR_SEVERITY.MEDIUM
        });
      }
    } catch (error) {
      console.error('Failed to sync KB configuration with backend:', error);
      throw error;
    }
  }

  /**
   * Load existing parrot data for editing mode
   * Requirements: 12.1, 12.2, 12.3 - Pre-populate existing knowledge base data
   * @returns {Promise<object|null>} Parrot wizard data including KB configuration
   */
  async loadExistingParrotData() {
    try {
      if (!this.parrotId) {
        console.warn('No parrot ID provided for loading existing data');
        return null;
      }

      console.log('Loading existing parrot data for editing:', this.parrotId);
      const response = await this.apiClient.get(`/api/parrots/${this.parrotId}/wizard-data`, {}, 'wizard-data');

      if (response.success && response.wizard_data) {
        const wizardData = response.wizard_data;
        
        // Extract and convert knowledge base configuration
        if (wizardData.step1 && wizardData.step1.knowledge_base) {
          const kbData = wizardData.step1.knowledge_base;
          
          // Convert to session manager format
          const kbConfig = {
            files: (kbData.files || []).map(file => ({
              id: file.id,
              name: file.name || file.original_filename,
              size: file.size || file.file_size,
              type: file.type || 'file',
              mime_type: file.mime_type,
              sync_status: file.sync_status || 'synced',
              selected: true, // Mark as selected for editing
              existing: true, // Mark as existing file
              created_at: file.created_at,
              updated_at: file.updated_at
            })),
            urls: kbData.url ? [{
              id: `url_${Date.now()}`,
              url: kbData.url,
              title: kbData.url_details?.title || kbData.url,
              status: 'validated',
              validated_at: kbData.url_details?.created_at || new Date().toISOString(),
              existing: true // Mark as existing URL
            }] : [],
            textContent: (kbData.text_documents || []).map(doc => ({
              id: doc.id,
              name: doc.name,
              content: doc.content_text || doc.content,
              sync_status: doc.sync_status || 'synced',
              selected: true, // Mark as selected for editing
              existing: true, // Mark as existing document
              created_at: doc.created_at,
              updated_at: doc.updated_at
            })),
            metadata: {
              last_updated: new Date().toISOString(),
              total_files: kbData.files?.length || 0,
              total_urls: kbData.url ? 1 : 0,
              total_text_items: kbData.text_documents?.length || 0,
              validation_status: 'valid',
              loaded_from_existing: true
            }
          };

          // Save the KB configuration
          this.saveKnowledgeBaseConfig(kbConfig);
          
          console.log('Pre-populated knowledge base configuration from existing parrot:', {
            filesCount: kbConfig.files.length,
            urlsCount: kbConfig.urls.length,
            textContentCount: kbConfig.textContent.length
          });
        }

        // Save step data to session
        if (wizardData.step1) {
          this.saveState(1, wizardData.step1, { completed: true });
        }
        if (wizardData.step2) {
          this.saveState(2, wizardData.step2, { completed: true });
        }

        this.notifyChangeListeners('existing_parrot_loaded', { 
          parrotId: this.parrotId,
          wizardData: wizardData 
        });
        
        return wizardData;
      }

      return null;
    } catch (error) {
      console.error('Failed to load existing parrot data:', error);
      return null;
    }
  }

  /**
   * Load KB configuration from backend session
   * @returns {Promise<object|null>} KB configuration from backend
   */
  async loadFromBackend() {
    try {
      // For editing mode, try to load existing parrot data first
      if (this.parrotId && !this.sessionId) {
        return await this.loadExistingParrotData();
      }

      if (!this.sessionId) {
        return null;
      }

      const response = await this.apiClient.get(`/api/wizard/session/${this.sessionId}`, {}, 'wizard-data');

      if (response.success && response.session && response.session.knowledge_base_config) {
        const backendConfig = JSON.parse(response.session.knowledge_base_config);
        
        // Merge with local configuration
        this.saveKnowledgeBaseConfig(backendConfig);
        this.notifyChangeListeners('kb_loaded_from_backend', { config: backendConfig });
        
        return backendConfig;
      }

      return null;
    } catch (error) {
      console.error('Failed to load KB configuration from backend:', error);
      return null;
    }
  }

  /**
   * Validate session state and KB configuration integrity
   * @returns {object} Validation result
   */
  validateSession() {
    const baseValidation = super.validateState();
    const kbConfig = this.getKnowledgeBaseConfig();
    
    const kbIssues = [];
    
    if (kbConfig) {
      // Validate file entries
      kbConfig.files.forEach((file, index) => {
        if (!file.id || !file.name) {
          kbIssues.push(`File ${index + 1}: Missing required fields (id or name)`);
        }
        if (file.size && file.size > 4 * 1024 * 1024) { // 4MB limit
          kbIssues.push(`File ${index + 1}: Exceeds 4MB size limit`);
        }
      });

      // Validate URL entries
      kbConfig.urls.forEach((url, index) => {
        if (!url.id || !url.url) {
          kbIssues.push(`URL ${index + 1}: Missing required fields (id or url)`);
        }
        const formatValidation = this.validateUrlFormat(url.url);
        if (!formatValidation.valid) {
          kbIssues.push(`URL ${index + 1}: ${formatValidation.error}`);
        }
      });

      // Validate text content entries
      kbConfig.textContent.forEach((text, index) => {
        if (!text.id || !text.content) {
          kbIssues.push(`Text ${index + 1}: Missing required fields (id or content)`);
        }
      });

      // Check metadata consistency
      if (kbConfig.metadata) {
        if (kbConfig.metadata.total_files !== kbConfig.files.length) {
          kbIssues.push('KB metadata: File count mismatch');
        }
        if (kbConfig.metadata.total_urls !== kbConfig.urls.length) {
          kbIssues.push('KB metadata: URL count mismatch');
        }
        if (kbConfig.metadata.total_text_items !== kbConfig.textContent.length) {
          kbIssues.push('KB metadata: Text content count mismatch');
        }
      }
    }

    return {
      valid: baseValidation.valid && kbIssues.length === 0,
      issues: [...baseValidation.issues, ...kbIssues],
      kbConfig: kbConfig
    };
  }

  /**
   * Set knowledge base enabled/disabled state
   * Requirements: 11.3, 11.4 - Handle knowledge base enabled/disabled state
   * @param {boolean} enabled - Whether knowledge base is enabled
   * @returns {boolean} Success status
   */
  setKnowledgeBaseEnabled(enabled) {
    try {
      const config = this.getKnowledgeBaseConfig() || this.defaultKbConfig;
      
      // Update the enabled state
      config.enabled = enabled;
      
      // If disabling, clear all KB data but keep the enabled flag
      if (!enabled) {
        config.files = [];
        config.urls = [];
        config.textContent = [];
        config.metadata = {
          ...this.defaultKbConfig.metadata,
          enabled: false,
          last_updated: new Date().toISOString()
        };
      } else {
        // If enabling, ensure metadata is properly initialized
        config.metadata = {
          ...config.metadata,
          enabled: true,
          last_updated: new Date().toISOString()
        };
      }
      
      const success = this.saveKnowledgeBaseConfig(config);
      
      if (success) {
        this.notifyChangeListeners('kb_enabled_changed', { enabled, config });
      }
      
      return success;
    } catch (error) {
      console.error('Failed to set knowledge base enabled state:', error);
      return false;
    }
  }

  /**
   * Get knowledge base enabled state
   * Requirements: 11.3, 11.4 - Handle knowledge base enabled/disabled state
   * @returns {boolean} Whether knowledge base is enabled
   */
  isKnowledgeBaseEnabled() {
    try {
      const config = this.getKnowledgeBaseConfig();
      if (!config) {
        return false;
      }
      
      // Check if explicitly disabled
      if (config.enabled === false) {
        return false;
      }
      
      // Auto-enable if there's any content and enabled state is not explicitly set
      if (config.enabled === undefined) {
        const hasContent = config.files.length > 0 || 
                          config.urls.length > 0 || 
                          config.textContent.length > 0;
        return hasContent;
      }
      
      return config.enabled === true;
    } catch (error) {
      console.error('Failed to get knowledge base enabled state:', error);
      return false;
    }
  }

  /**
   * Clear KB configuration from session
   * Requirements: 11.2 - Clear URL and file selections when knowledge base is disabled
   * @returns {boolean} Success status
   */
  clearKnowledgeBaseConfig() {
    try {
      localStorage.removeItem(this.kbStorageKey);
      this.kbValidationCache.clear();
      this.notifyChangeListeners('kb_config_cleared', {});
      return true;
    } catch (error) {
      console.error('Failed to clear KB configuration:', error);
      return false;
    }
  }

  /**
   * Clear all session data including KB configuration
   * @returns {boolean} Success status
   */
  clearState() {
    const baseResult = super.clearState();
    const kbResult = this.clearKnowledgeBaseConfig();
    return baseResult && kbResult;
  }

  /**
   * Get summary of KB configuration for display
   * @returns {object} KB configuration summary
   */
  getKnowledgeBaseSummary() {
    const config = this.getKnowledgeBaseConfig();
    if (!config) {
      return {
        hasContent: false,
        files: 0,
        urls: 0,
        textItems: 0,
        validationStatus: 'none'
      };
    }

    return {
      hasContent: config.files.length > 0 || config.urls.length > 0 || config.textContent.length > 0,
      files: config.files.length,
      urls: config.urls.length,
      textItems: config.textContent.length,
      validationStatus: config.metadata.validation_status || 'unknown',
      lastUpdated: config.metadata.last_updated
    };
  }

  /**
   * Prepare knowledge base configuration for wizard finalization
   * Requirements: 11.3, 11.4, 12.5, 12.6 - Handle KB enable/disable and editing scenarios
   * @param {boolean} isEditing - Whether this is editing an existing parrot
   * @param {boolean} kbEnabled - Whether knowledge base is enabled
   * @returns {object} Finalization-ready KB configuration
   */
  prepareKnowledgeBaseForFinalization(isEditing = false, kbEnabled = false) {
    try {
      const config = this.getKnowledgeBaseConfig() || this.defaultKbConfig;
      
      // If knowledge base is disabled, return empty configuration
      if (!kbEnabled) {
        console.log('Knowledge base disabled - returning empty configuration for finalization');
        return {
          enabled: false,
          files: [],
          urls: [],
          textContent: [],
          metadata: {
            ...config.metadata,
            enabled: false,
            finalization_mode: isEditing ? 'edit_disable' : 'create_disable',
            prepared_at: new Date().toISOString()
          }
        };
      }
      
      // If knowledge base is enabled, prepare the configuration
      const finalizedConfig = {
        enabled: true,
        files: (config.files || []).map(file => ({
          ...file,
          // Mark files for proper handling during finalization
          selected: file.selected !== false,
          existing: file.existing || false,
          finalization_ready: true
        })),
        urls: (config.urls || []).map(url => ({
          ...url,
          finalization_ready: true
        })),
        textContent: (config.textContent || []).map(text => ({
          ...text,
          finalization_ready: true
        })),
        metadata: {
          ...config.metadata,
          enabled: true,
          finalization_mode: isEditing ? 'edit_enable' : 'create_enable',
          prepared_at: new Date().toISOString(),
          total_files: config.files?.length || 0,
          total_urls: config.urls?.length || 0,
          total_text_items: config.textContent?.length || 0
        }
      };
      
      console.log('Knowledge base configuration prepared for finalization:', {
        enabled: finalizedConfig.enabled,
        filesCount: finalizedConfig.files.length,
        urlsCount: finalizedConfig.urls.length,
        textContentCount: finalizedConfig.textContent.length,
        mode: finalizedConfig.metadata.finalization_mode
      });
      
      return finalizedConfig;
    } catch (error) {
      console.error('Failed to prepare knowledge base configuration for finalization:', error);
      return {
        enabled: false,
        files: [],
        urls: [],
        textContent: [],
        metadata: {
          enabled: false,
          finalization_mode: 'error',
          error: error.message,
          prepared_at: new Date().toISOString()
        }
      };
    }
  }

  /**
   * Handle knowledge base configuration changes during editing
   * Requirements: 12.5, 12.6 - Change detection for editing scenarios
   * @param {object} originalConfig - Original KB configuration from existing parrot
   * @param {object} currentConfig - Current KB configuration from session
   * @returns {object} Change detection results
   */
  detectKnowledgeBaseChanges(originalConfig, currentConfig) {
    try {
      const changes = {
        hasChanges: false,
        enabledChanged: false,
        filesChanged: false,
        urlChanged: false,
        textContentChanged: false,
        details: {
          originalEnabled: originalConfig?.enabled || false,
          currentEnabled: currentConfig?.enabled || false,
          originalFiles: originalConfig?.files || [],
          currentFiles: currentConfig?.files || [],
          originalUrl: originalConfig?.url || '',
          currentUrl: currentConfig?.url || '',
          originalTextContent: originalConfig?.textContent || [],
          currentTextContent: currentConfig?.textContent || []
        }
      };
      
      // Check if enabled state changed
      changes.enabledChanged = changes.details.originalEnabled !== changes.details.currentEnabled;
      
      // Check if files changed
      const originalFileIds = changes.details.originalFiles.map(f => f.id).sort();
      const currentFileIds = changes.details.currentFiles.filter(f => f.selected !== false).map(f => f.id).sort();
      changes.filesChanged = JSON.stringify(originalFileIds) !== JSON.stringify(currentFileIds);
      
      // Check if URL changed
      changes.urlChanged = changes.details.originalUrl.trim() !== changes.details.currentUrl.trim();
      
      // Check if text content changed
      const originalTextIds = changes.details.originalTextContent.map(t => t.id).sort();
      const currentTextIds = changes.details.currentTextContent.map(t => t.id).sort();
      changes.textContentChanged = JSON.stringify(originalTextIds) !== JSON.stringify(currentTextIds);
      
      // Determine if there are any changes
      changes.hasChanges = changes.enabledChanged || changes.filesChanged || changes.urlChanged || changes.textContentChanged;
      
      console.log('Knowledge base change detection results:', {
        hasChanges: changes.hasChanges,
        enabledChanged: changes.enabledChanged,
        filesChanged: changes.filesChanged,
        urlChanged: changes.urlChanged,
        textContentChanged: changes.textContentChanged,
        originalFileCount: originalFileIds.length,
        currentFileCount: currentFileIds.length
      });
      
      return changes;
    } catch (error) {
      console.error('Failed to detect knowledge base changes:', error);
      return {
        hasChanges: true, // Assume changes if detection fails
        enabledChanged: true,
        filesChanged: true,
        urlChanged: true,
        textContentChanged: true,
        error: error.message,
        details: {}
      };
    }
  }

  /**
   * Export KB configuration for debugging
   * @returns {object} Exported KB data
   */
  exportKnowledgeBaseConfig() {
    return {
      kbConfig: this.getKnowledgeBaseConfig(),
      summary: this.getKnowledgeBaseSummary(),
      validation: this.validateSession(),
      metadata: {
        sessionId: this.sessionId,
        parrotId: this.parrotId,
        storageKey: this.kbStorageKey,
        exportedAt: new Date().toISOString()
      }
    };
  }
}

/**
 * Create wizard session manager instance with KB support
 * @param {string} sessionId - Wizard session ID
 * @param {string} parrotId - Parrot ID (for editing existing parrots)
 * @param {object} apiClient - API client instance
 * @returns {WizardSessionManager} Session manager instance
 */
export const createWizardSessionManager = (sessionId = null, parrotId = null, apiClient = null) => {
  return new WizardSessionManager(sessionId, parrotId, apiClient);
};

/**
 * React hook for using wizard session manager with KB support
 * @param {string} sessionId - Wizard session ID
 * @param {string} parrotId - Parrot ID (for editing existing parrots)
 * @param {Function} getToken - Function to get auth token
 * @returns {WizardSessionManager} Session manager instance
 */
export const useWizardSessionManager = (sessionId = null, parrotId = null, getToken = null) => {
  const [sessionManager] = React.useState(() => {
    const manager = createWizardSessionManager(sessionId, parrotId);
    if (getToken) {
      manager.setAuthTokenGetter(getToken);
    }
    return manager;
  });
  
  React.useEffect(() => {
    // Update manager IDs if they change
    if (sessionId !== sessionManager.sessionId || parrotId !== sessionManager.parrotId) {
      sessionManager.sessionId = sessionId;
      sessionManager.parrotId = parrotId;
      sessionManager.storageKey = sessionManager.getStorageKey();
      sessionManager.kbStorageKey = `${sessionManager.storageKey}_kb_config`;
    }
    
    // Update auth token getter if it changes
    if (getToken && sessionManager.getAuthToken !== getToken) {
      sessionManager.setAuthTokenGetter(getToken);
    }
  }, [sessionId, parrotId, getToken, sessionManager]);

  return sessionManager;
};

// Export the original WizardStateManager for backward compatibility
export { WizardStateManager, WizardStateUtils } from './wizardStateManager.js';